import { isAxiosError } from "axios";
import { realityId } from "@/utils/reality-id";
import { isDevSubscriptionBypassEnabled } from "@/utils/dev-subscription-bypass";
import api from "../api";
import {
  EmailValidationResponse,
  SignInResponse,
  SignUpLitePayload,
  SignUpPayload,
} from "./types/auth.types";

type SignUpResponse = {
  user?: {
    id?: number;
  };
};

const LEGACY_SIGNUP_LITE_FALLBACK = {
  occupationField: "Não informado",
  profession: "Não informado",
  timeOfProfession: "Não informado",
};

/** LB/proxy cold start, timeouts, rate limits; also retry transient network failures. */
const FIREBASE_SIGNUP_MAX_ATTEMPTS = 4;
/** 500 included: first firebase-signup after sign-up-lite sometimes hits cold LB / transient GCP; request is idempotent. */
const RETRYABLE_EDGE_STATUS = new Set([408, 429, 500, 502, 503, 504]);

function isTransientFirebaseSignupError(err: unknown): boolean {
  if (!isAxiosError(err)) {
    return false;
  }
  const status = err.response?.status;
  if (status !== undefined) {
    return RETRYABLE_EDGE_STATUS.has(status);
  }
  // No response: network, CORS-like abort, DNS, etc.
  return err.code === "ECONNABORTED" || err.code === "ERR_NETWORK" || !err.response;
}

export const firebaseSignup = async (phone: string, userId?: number) => {
  const encodedPhone = encodeURIComponent(phone);
  const userIdQuery = userId ? `&userId=${userId}` : "";
  const url = `auth/firebase-signup/${encodedPhone}?realityId=${realityId}${userIdQuery}`;

  let lastError: unknown;
  for (let attempt = 1; attempt <= FIREBASE_SIGNUP_MAX_ATTEMPTS; attempt++) {
    try {
      const res = await api.post(url);
      return res.data;
    } catch (err) {
      lastError = err;
      const canRetry =
        attempt < FIREBASE_SIGNUP_MAX_ATTEMPTS && isTransientFirebaseSignupError(err);
      if (!canRetry) {
        throw err;
      }
      await new Promise((r) => setTimeout(r, 350 * attempt));
    }
  }
  throw lastError;
};

export const getEmailValidation = async (
  email: string
): Promise<EmailValidationResponse | undefined> => {
  const res = await api.post<EmailValidationResponse>(`auth/email-validation`, {
    email,
    realityId,
  });

  return res.data;
};

export const signUp = async (
  payload: SignUpPayload
): Promise<SignUpResponse> => {
  const res = await api.post<SignUpResponse>(`auth/sign-up`, {
    ...payload,
    realityId,
  });

  return res.data;
};

export const signUpLite = async (
  payload: SignUpLitePayload
): Promise<SignUpResponse> => {
  const shouldBypassSubscriptionLock = isDevSubscriptionBypassEnabled();
  const endpoint = shouldBypassSubscriptionLock
    ? `auth/sign-up-lite?devSubscriptionBypass=true`
    : `auth/sign-up-lite`;

  const res = await api.post<SignUpResponse>(endpoint, {
    ...payload,
    // Backward compatibility for environments still validating legacy fields in sign-up-lite.
    ...LEGACY_SIGNUP_LITE_FALLBACK,
    realityId,
  });

  return res.data;
};

export const signIn = async (
  email: string,
  password: string
): Promise<SignInResponse> => {
  const res = await api.post<SignInResponse>(`auth/sign-in`, {
    email,
    password,
    realityId,
  });

  return res.data;
};

export const sendPasswordRecoveryCode = async (
  email: string
): Promise<void> => {
  return api.post(`auth/password-recovery/request`, {
    email,
  });
};

export const changeUserPassword = async (
  email: string,
  verificationCode: string,
  newPassword: string
): Promise<void> => {
  return api.post(`auth/password-recovery/reset-verification`, {
    email,
    verificationCode,
    newPassword,
  });
};

export const sendWhatsappCode = async (phone: string): Promise<void> => {
  const encodedPhone = encodeURIComponent(phone);
  return api.post(
    `auth/send-otp-whatsapp/${encodedPhone}?realityId=${realityId}`
  );
};

export const validateWhatsappCode = async (phone: string, code: string) => {
  const encodedPhone = encodeURIComponent(phone);
  const encodedCode = encodeURIComponent(code);
  const res = await api.get<{
    isValid: boolean;
    isExpired: boolean;
    accessToken: string;
  }>(
    `auth/validate-otp/${encodedPhone}/${encodedCode}?realityId=${realityId}`
  );

  return res.data;
};
